// ── HivePulse — Feature L (feature_hive_pulse) ───────────────────────────────
// Real-time-ish header strip showing online users, breakdown by mood,
// and "available 24h" count for the active network. Polls every 60s.

import { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { fetchHivePulse } from '../utils/featurePackA';

const MOOD_DOTS = {
  growing:    { emoji: '🌱', fr: 'En évolution', en: 'Growing' },
  exploring:  { emoji: '🧭', fr: 'Curieux',      en: 'Exploring' },
  ready:      { emoji: '✨', fr: 'Prêt·e',       en: 'Ready' },
  connecting: { emoji: '💛', fr: 'En lien',      en: 'Connecting' },
  lowkey:     { emoji: '🌙', fr: 'Discret',      en: 'Lowkey' },
};

export default function HivePulse({ bh }) {
  const { features, activeNetwork, lang } = useApp();
  const [pulse, setPulse] = useState(null);

  useEffect(() => {
    if (!features?.feature_hive_pulse || !activeNetwork) return;
    let cancelled = false;
    const tick = async () => {
      const p = await fetchHivePulse(activeNetwork);
      if (!cancelled) setPulse(p);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [features?.feature_hive_pulse, activeNetwork]);

  if (!features?.feature_hive_pulse || !pulse) return null;
  const fr = lang === 'fr';
  const moods = pulse.mood_breakdown || {};
  const topMoods = Object.entries(moods)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
  const totalMood = topMoods.reduce((s, [, n]) => s + n, 0) || 1;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', margin: '0.9rem 1rem 0',
      background: bh?.card || 'rgba(201,168,76,0.07)',
      border: `1px solid ${bh?.border || 'rgba(201,168,76,0.18)'}`,
      borderRadius: 8, flexWrap: 'wrap', fontFamily: 'Outfit',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%', background: '#4EC994',
          boxShadow: '0 0 0 3px rgba(78,201,148,0.18)',
          animation: 'pulse-dot 2s ease-in-out infinite',
        }}/>
        <span style={{ fontSize: 12, color: bh?.text || '#F2E8D0', fontWeight: 600 }}>
          {pulse.online_now || 0} {fr ? 'en ligne' : 'online'}
        </span>
        <span style={{ fontSize: 11, color: bh?.sub || '#8A7A5A' }}>
          · {pulse.active_today || 0} {fr ? "aujourd'hui" : 'today'}
        </span>
      </div>

      {topMoods.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {topMoods.map(([mood, count]) => {
            const m = MOOD_DOTS[mood] || { emoji: '·', fr: mood, en: mood };
            const pct = Math.round((count / totalMood) * 100);
            return (
              <span key={mood}
                title={fr ? m.fr : m.en}
                style={{ fontSize: 11, color: bh?.sub || '#8A7A5A', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                {m.emoji} {pct}%
              </span>
            );
          })}
        </div>
      )}

      {pulse.available_now > 0 && (
        <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>
          ✦ {pulse.available_now} {fr ? 'disponible(s)' : 'available'}
        </span>
      )}
    </div>
  );
}
